/**
 * Spec 108 — Cross-Lens Persona Diagnosis. Typed client for the brand-scoped
 * diagnosis routes (Part 1: the diagnostic spine — no narrative/recommendations,
 * those are spec 109).
 *
 *   POST /api/v1/brands/:brandId/persona-diagnoses
 *   GET  /api/v1/brands/:brandId/persona-diagnoses
 *   GET  /api/v1/brands/:brandId/persona-diagnoses/:diagnosisId
 *
 * The three lenses (GEO 083, response persona-fit 106, page persona-fit 107) are
 * held side by side and NEVER blended into a single number.
 */
import { apiGet } from '../lib/api.js';
import { apiPost } from '../lib/api-mutations.js';

/* ------------------------------------------------------------------ */
/* Spec 109 — synthesis types (recommendations + quotes + ranked list) */
/* ------------------------------------------------------------------ */

export type RecommendationKind =
  | 'reframe_existing'
  | 'add_trust_evidence'
  | 'remove_anti_signals'
  | 'expand_existing'
  | 'new_page'
  | 'competitive_response';

export type RecommendationPriority = 'critical' | 'high' | 'medium' | 'low';

export interface SpecificChange {
  before_quote: string;
  suggested_after: string;
  rationale: string;
}

export interface PersonaDiagnosisRecommendation {
  id: string;
  persona_diagnosis_id: string;
  target_page_url: string | null;
  recommendation_kind: RecommendationKind;
  persona_grounding: string;
  specific_change: SpecificChange;
  priority: RecommendationPriority;
  distinct_from_geo: string;
  sort_order: number;
  created_at: string;
}

export interface QuoteUsed {
  page_url: string;
  quote: string;
  lens_ref: string;
}

export type DiagnosisStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'insufficient_data'
  | 'failed';

export type DiagnosisType =
  | 'content_quality'
  | 'geo_and_quality'
  | 'audience_mismatch'
  | 'content_gap'
  | 'competitive_displacement';

export type DiagnosisConfidence = 'high' | 'moderate' | 'low' | 'very_low';

export type LinkageSignal =
  | 'actual_citation'
  | 'fanout_match'
  | 'absence_inference';

export interface ImplicatedPage {
  page_url: string;
  linkage_signal: LinkageSignal;
  linkage_confidence: number;
  page_persona_fit_id: string | null;
  geo_rules_eval_id: string | null;
  geo_score: number | null;
  persona_fit_score: number | null;
  competitor_cited: boolean;
}

export interface PersonaDiagnosis {
  id: string;
  brand_id: string;
  persona_id: string;
  persona_stable_id: string;
  persona_version: number;
  persona_confidence_at_eval: 'survey_grounded' | 'research_informed' | 'hypothesis';
  persona_type: 'commercial' | 'informational';
  query_cluster_id: string;
  status: DiagnosisStatus;
  response_finding_id: string | null;
  observed_visibility: number | null;
  response_evidence_strength: Record<string, unknown> | null;
  implicated_pages: ImplicatedPage[];
  diagnosis_type: DiagnosisType | null;
  coverage_gap_context_id: string | null;
  combined_confidence: DiagnosisConfidence | null;
  confidence_limiting_factor: string | null;
  triggered_by_user: string | null;
  created_at: string;
  completed_at: string | null;
  // ── Spec 109 (synthesis) ──
  diagnosis_narrative: string | null;
  narrative_generated_at: string | null;
  quotes_used: QuoteUsed[];
}

/** Spec 109 — the full deliverable: a diagnosis plus its ordered recommendations. */
export interface PersonaDiagnosisFull extends PersonaDiagnosis {
  recommendations: PersonaDiagnosisRecommendation[];
}

export interface PersonaDiagnosisListItem {
  id: string;
  status: DiagnosisStatus;
  diagnosis_type: DiagnosisType | null;
  combined_confidence: DiagnosisConfidence | null;
  persona_id: string;
  query_cluster_id: string;
  created_at: string;
}

/** Spec 109 — summary item ranked by severity x confidence. */
export interface PersonaDiagnosisRankedItem extends PersonaDiagnosisListItem {
  observed_visibility: number | null;
  severity: number;
  rank_score: number;
  has_narrative: boolean;
}

export async function listPersonaDiagnoses(
  brandId: string,
  opts: { personaId?: string; status?: string; limit?: number } = {},
): Promise<PersonaDiagnosisListItem[]> {
  const params = new URLSearchParams();
  if (opts.personaId) params.set('persona_id', opts.personaId);
  if (opts.status) params.set('status', opts.status);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const res = await apiGet<{ data: PersonaDiagnosisListItem[] }>(
    `/api/v1/brands/${brandId}/persona-diagnoses${qs ? `?${qs}` : ''}`,
  );
  return res.data;
}

export async function listPersonaDiagnosesRanked(
  brandId: string,
  opts: { personaId?: string; status?: string; limit?: number } = {},
): Promise<PersonaDiagnosisRankedItem[]> {
  const params = new URLSearchParams();
  if (opts.personaId) params.set('persona_id', opts.personaId);
  if (opts.status) params.set('status', opts.status);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const res = await apiGet<{ data: PersonaDiagnosisRankedItem[] }>(
    `/api/v1/brands/${brandId}/persona-diagnoses${qs ? `?${qs}` : ''}`,
  );
  return res.data;
}

export async function getPersonaDiagnosis(
  brandId: string,
  diagnosisId: string,
): Promise<PersonaDiagnosisFull> {
  const res = await apiGet<{ data: PersonaDiagnosisFull }>(
    `/api/v1/brands/${brandId}/persona-diagnoses/${diagnosisId}`,
  );
  return res.data;
}

export async function synthesizePersonaDiagnosis(
  brandId: string,
  diagnosisId: string,
): Promise<{ diagnosis_id: string; narrative_generated_at: string | null; recommendation_count: number }> {
  const res = await apiPost<{
    data: { diagnosis_id: string; narrative_generated_at: string | null; recommendation_count: number };
  }>(`/api/v1/brands/${brandId}/persona-diagnoses/${diagnosisId}/synthesize`, {});
  return res.data;
}

export async function createPersonaDiagnosis(
  brandId: string,
  body: { persona_id: string; query_cluster_id: string },
): Promise<{ diagnosis_id: string; status: DiagnosisStatus }> {
  const res = await apiPost<{ data: { diagnosis_id: string; status: DiagnosisStatus } }>(
    `/api/v1/brands/${brandId}/persona-diagnoses`,
    body,
  );
  return res.data;
}

/* ------------------------------------------------------------------ */
/* Display helpers                                                     */
/* ------------------------------------------------------------------ */

export const DIAGNOSIS_TYPE_LABEL: Record<DiagnosisType, string> = {
  audience_mismatch: 'Audience mismatch: GEO-strong, persona-weak',
  geo_and_quality: 'GEO and quality: weak on both lenses',
  content_quality: 'Content quality: page present, mixed fit',
  content_gap: 'Content gap: no page positioned for this persona',
  competitive_displacement: 'Competitive displacement: a competitor page wins',
};

export const LINKAGE_SIGNAL_LABEL: Record<LinkageSignal, string> = {
  actual_citation: 'Actual citation',
  fanout_match: 'Fan-out match',
  absence_inference: 'Absence inference',
};

export const RECOMMENDATION_KIND_LABEL: Record<RecommendationKind, string> = {
  reframe_existing: 'Reframe existing page',
  add_trust_evidence: 'Add trust evidence',
  remove_anti_signals: 'Remove anti-signals',
  expand_existing: 'Expand existing page',
  new_page: 'New page (content gap)',
  competitive_response: 'Competitive response',
};

export const RECOMMENDATION_PRIORITY_LABEL: Record<RecommendationPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};
